/**
 * TitanReportingDashboard — Navigator's Titan Treasure reporting page (DS-238).
 *
 * Routes: /titan-reporting and /titan-reporting/:panelKey. Shown only for the
 * Titan Treasure realm (see isTitanReportingRealm in ttApi.js and the gate in
 * Navigator.js). The tabs come from panelRegistry.js. Styled like the Titan
 * Treasure fraud review pages: a blue banner, then a white tab bar with the
 * open tab in blue.
 *
 * URL state: `window` (1h | 24h | 7d) is kept across tabs; panel
 * state such as `account` is dropped when switching tabs. `realm` is always
 * kept, because Navigator reads it from the URL.
 */
import React, { useCallback, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { layout } from "../../design/tokens";
import { DEFAULT_WINDOW, WINDOW_OPTIONS } from "./ttApi";
import { TT_PANELS, panelByKey } from "./panelRegistry";
import { INK, TONE } from "./ttKit";

const KEEP_ACROSS_TABS = ["realm", "window"];

const HERO_BG = "linear-gradient(100deg, #16286B 0%, #2563EB 62%, #3B82F6 100%)";
const onHero = { background: "rgba(255,255,255,0.18)", color: "#FFFFFF" };
const heroPill = {
  ...onHero,
  border: "none",
  borderRadius: 999,
  padding: "4px 11px",
  fontSize: 12,
  fontFamily: "inherit",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  whiteSpace: "nowrap",
};

export default function TitanReportingDashboard() {
  const { panelKey } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [refreshKey, setRefreshKey] = useState(0);

  const panel = panelByKey(panelKey);
  const win = WINDOW_OPTIONS.some((o) => o.value === params.get("window")) ? params.get("window") : DEFAULT_WINDOW;

  const goTo = useCallback(
    (key, extra = {}) => {
      const next = new URLSearchParams();
      KEEP_ACROSS_TABS.forEach((k) => params.get(k) && next.set(k, params.get(k)));
      Object.entries(extra).forEach(([k, v]) => v && next.set(k, v));
      navigate({ pathname: `/titan-reporting/${key}`, search: `?${next.toString()}` });
    },
    [navigate, params]
  );

  const setWindow = (value) => {
    const next = new URLSearchParams(params);
    next.set("window", value);
    setParams(next);
  };

  const openAccount = useCallback((email) => goTo("users", { account: email }), [goTo]);
  const openRing = useCallback((id) => goTo("rings", { ring: id }), [goTo]);

  const Panel = panel.component;

  return (
    <div style={{ padding: 20, minHeight: "100vh", background: INK.page, color: INK.ink }}>
      <div style={{ maxWidth: layout.contentMaxWidth, margin: "0 auto" }}>
        <header
          style={{
            background: HERO_BG,
            color: "#FFFFFF",
            borderRadius: 14,
            padding: "20px 22px",
            display: "flex",
            flexWrap: "wrap",
            gap: 14,
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 18,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 13, minWidth: 0 }}>
            <span
              style={{
                ...onHero,
                background: "rgba(255,255,255,0.17)",
                width: 38, height: 38, borderRadius: 9, flex: "none",
                display: "grid", placeItems: "center", fontWeight: 700, fontSize: 19,
              }}
            >
              I
            </span>
            <div style={{ minWidth: 0 }}>
              <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600, letterSpacing: "-0.01em" }}>Idenza Command Center</h1>
              <p style={{ margin: "3px 0 0", fontSize: 13, color: "rgba(255,255,255,0.84)", maxWidth: "62ch" }}>
                Titan Treasure, on every card deposit since 15 September 2026. {panel.description}
              </p>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
            <span style={heroPill}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#4ADE80" }} />
              Titan Treasure
            </span>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
              {panel.allTime ? (
                <span style={heroPill}>All time</span>
              ) : (
                <div role="group" aria-label="Time window" style={{ display: "flex", gap: 4 }}>
                  {WINDOW_OPTIONS.map((o) => {
                    const active = o.value === win;
                    return (
                      <button
                        key={o.value}
                        type="button"
                        aria-pressed={active}
                        onClick={() => setWindow(o.value)}
                        style={{
                          ...heroPill,
                          cursor: "pointer",
                          ...(active && { background: "#FFFFFF", color: TONE.blue.text, fontWeight: 600 }),
                        }}
                      >
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              )}
              <button type="button" onClick={() => setRefreshKey((k) => k + 1)} style={{ ...heroPill, cursor: "pointer" }}>
                <RefreshCw size={13} strokeWidth={1.75} />
                Refresh
              </button>
            </div>
          </div>
        </header>

        <nav
          role="tablist"
          aria-label="Titan Treasure panels"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
            background: INK.panel,
            border: `1px solid ${INK.line}`,
            borderRadius: 10,
            padding: 4,
            marginBottom: 18,
          }}
        >
          {TT_PANELS.map((p) => (
            <TabButton key={p.key} active={p.key === panel.key} onClick={() => goTo(p.key)}>
              {p.label}
            </TabButton>
          ))}
        </nav>

        <Panel key={panel.key} win={win} fresh={refreshKey || undefined} openAccount={openAccount} openRing={openRing} />
      </div>
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        border: "none",
        borderRadius: 7,
        padding: "7px 13px",
        fontFamily: "inherit",
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        whiteSpace: "nowrap",
        cursor: "pointer",
        background: active ? TONE.blue.fg : hover ? INK.track : "none",
        color: active ? "#FFFFFF" : hover ? INK.ink2 : INK.muted,
      }}
    >
      {children}
    </button>
  );
}
